import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { API } from "@/lib/api";
import { openTransportPdf } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Truck, ClipboardList, Users, TrendingUp, DollarSign, Search, Download, Trash2, Archive, Plus, Building2, MapPin, UserRound, Car, Pencil, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [zipOpen, setZipOpen] = useState(false);
  const [zipStart, setZipStart] = useState("");
  const [zipEnd, setZipEnd] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState("");
  const [clientBusy, setClientBusy] = useState(false);
  const [locations, setLocations] = useState([]);
  const [newLoc, setNewLoc] = useState({ name: "", address: "", city: "" });
  const [locBusy, setLocBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "" });
  const [contactBusy, setContactBusy] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null); // { kind, data }
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(()=>{});
    api.get("/transports?all=true").then((r) => setItems(r.data)).catch(()=>{});
    api.get("/clients").then((r) => setClients(r.data)).catch(()=>{});
    api.get("/locations").then((r) => setLocations(r.data)).catch(()=>{});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(()=>{});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(()=>{});
  }, []);

  const addClient = async () => {
    const name = newClient.trim();
    if (!name) return;
    setClientBusy(true);
    try {
      const { data } = await api.post("/clients", { name });
      setClients((cs) => {
        if (cs.some((c) => c.id === data.id)) return cs;
        return [...cs, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setNewClient("");
      toast.success("Client ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setClientBusy(false);
    }
  };

  const delClient = async (id) => {
    if (!window.confirm("Supprimer ce client de la liste ?")) return;
    try {
      await api.delete(`/clients/${id}`);
      setClients((cs) => cs.filter((c) => c.id !== id));
      toast.success("Client supprimé");
    } catch {
      toast.error("Échec");
    }
  };

  const addLocation = async () => {
    const name = newLoc.name.trim();
    if (!name) return;
    setLocBusy(true);
    try {
      const { data } = await api.post("/locations", {
        name,
        address: newLoc.address.trim(),
        city: newLoc.city.trim(),
      });
      setLocations((ls) => {
        const filtered = ls.filter((l) => l.id !== data.id);
        return [...filtered, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setNewLoc({ name: "", address: "", city: "" });
      toast.success("Lieu ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setLocBusy(false);
    }
  };

  const delLocation = async (id) => {
    if (!window.confirm("Supprimer ce lieu de la liste ?")) return;
    try {
      await api.delete(`/locations/${id}`);
      setLocations((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lieu supprimé");
    } catch {
      toast.error("Échec");
    }
  };

  const addContact = async () => {
    const name = newContact.name.trim();
    if (!name) return;
    setContactBusy(true);
    try {
      const { data } = await api.post("/contacts", {
        name,
        email: newContact.email.trim(),
        phone: newContact.phone.trim(),
      });
      setContacts((cs) => {
        const filtered = cs.filter((c) => c.id !== data.id);
        return [...filtered, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setNewContact({ name: "", email: "", phone: "" });
      toast.success("Contact ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setContactBusy(false);
    }
  };

  const delContact = async (id) => {
    if (!window.confirm("Supprimer ce contact de la liste ?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast.success("Contact supprimé");
    } catch {
      toast.error("Échec");
    }
  };

  const delVehicle = async (id) => {
    if (!window.confirm("Oublier ce véhicule mémorisé ?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      setVehicles((vs) => vs.filter((v) => v.id !== id));
      toast.success("Véhicule oublié");
    } catch {
      toast.error("Échec");
    }
  };

  const toggleInvoiceSent = async (t) => {
    const next = !t.invoice_sent;
    // Optimistic update
    setItems((xs) => xs.map((x) => (x.id === t.id ? { ...x, invoice_sent: next } : x)));
    try {
      await api.patch(`/transports/${t.id}/invoice-sent`, { invoice_sent: next });
      toast.success(next ? "Marqué comme facturé" : "Marqué comme non facturé");
    } catch {
      // revert
      setItems((xs) => xs.map((x) => (x.id === t.id ? { ...x, invoice_sent: t.invoice_sent } : x)));
      toast.error("Échec");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditBusy(true);
    try {
      const { kind, data } = editing;
      if (kind === "client") {
        const { data: r } = await api.put(`/clients/${data.id}`, { name: data.name });
        setClients((cs) => cs.map((c) => (c.id === r.id ? r : c)).sort((a, b) => a.name.localeCompare(b.name)));
      } else if (kind === "location") {
        const { data: r } = await api.put(`/locations/${data.id}`, {
          name: data.name, address: data.address || "", city: data.city || "",
        });
        setLocations((ls) => ls.map((l) => (l.id === r.id ? r : l)).sort((a, b) => a.name.localeCompare(b.name)));
      } else if (kind === "contact") {
        const { data: r } = await api.put(`/contacts/${data.id}`, {
          name: data.name, email: data.email || "", phone: data.phone || "",
        });
        setContacts((cs) => cs.map((c) => (c.id === r.id ? r : c)).sort((a, b) => a.name.localeCompare(b.name)));
      } else if (kind === "vehicle") {
        const { data: r } = await api.put(`/vehicles/${data.id}`, {
          fleet_number: data.fleet_number,
          vehicle_type: data.vehicle_type || "",
          vehicle_make_model: data.vehicle_make_model || "",
          vehicle_year: data.vehicle_year || "",
          vehicle_color: data.vehicle_color || "",
          vehicle_plate: data.vehicle_plate || "",
          vehicle_vin: data.vehicle_vin || "",
        });
        setVehicles((vs) => vs.map((v) => (v.id === r.id ? { ...v, ...r } : v)).sort((a, b) => a.fleet_number.localeCompare(b.fleet_number)));
      }
      toast.success("Modifié");
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setEditBusy(false);
    }
  };

  const editKindTitle = {
    client: "Modifier le client",
    location: "Modifier le lieu",
    contact: "Modifier le contact",
    vehicle: "Modifier le véhicule",
  };

  const filtered = items.filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [t.client_name, t.po_number, t.vehicle_make_model, t.vehicle_plate, t.driver_email, t.id]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
  });

  const del = async (id) => {
    if (!window.confirm("Supprimer ce transport ?")) return;
    try {
      await api.delete(`/transports/${id}`);
      setItems((xs) => xs.filter((t) => t.id !== id));
      toast.success("Supprimé");
    } catch (e) {
      toast.error("Échec");
    }
  };

  const downloadZip = async () => {
    setZipBusy(true);
    try {
      const params = new URLSearchParams();
      if (zipStart) params.set("start", zipStart);
      if (zipEnd) params.set("end", zipEnd);
      const r = await api.get(`/admin/transports/zip?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const tag = (zipStart || zipEnd) ? `_${zipStart || "debut"}_a_${zipEnd || "fin"}` : "";
      a.download = `transports_transprestige${tag}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Archive ZIP téléchargée");
      setZipOpen(false);
    } catch (err) {
      const msg = err.response?.status === 404 ? "Aucun transport dans cette période" : "Échec du téléchargement";
      toast.error(msg);
    } finally {
      setZipBusy(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-8">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Console administrateur</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">Tableau de bord</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="new-transport-btn"
            className="h-12 px-5 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-bold btn-hard active:scale-95"
            onClick={() => window.location.assign("/admin/transports/nouveau")}
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau bon de transport
          </Button>
          <Dialog open={zipOpen} onOpenChange={setZipOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="zip-btn"
                className="h-12 px-5 rounded-none bg-black text-white hover:bg-black/80 font-bold btn-hard active:scale-95"
              >
                <Archive className="w-4 h-4 mr-2" /> Archiver PDFs (ZIP)
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-2">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">Télécharger l'archive PDF</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Téléchargez d'un seul coup tous les bons de transport en PDF, regroupés dans un fichier ZIP.
                  Laissez les dates vides pour tout télécharger, ou filtrez sur une période.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="label-caps">Date début</Label>
                    <Input
                      data-testid="zip-start"
                      type="date"
                      value={zipStart}
                      onChange={(e) => setZipStart(e.target.value)}
                      className="h-12 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Date fin</Label>
                    <Input
                      data-testid="zip-end"
                      type="date"
                      value={zipEnd}
                      onChange={(e) => setZipEnd(e.target.value)}
                      className="h-12 border-2 rounded-none"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Nommage des fichiers : <span className="font-mono-alt">AAAA-MM-JJ_client_plaque_ID.pdf</span> — tri chronologique automatique.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-none border-2" onClick={() => setZipOpen(false)}>Annuler</Button>
                <Button
                  data-testid="zip-download"
                  disabled={zipBusy}
                  onClick={downloadZip}
                  className="rounded-none bg-primary text-primary-foreground btn-hard"
                >
                  {zipBusy ? "Préparation…" : (<><Download className="w-4 h-4 mr-2" /> Télécharger ZIP</>)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat
            icon={DollarSign}
            label={`Revenu ${stats?.month || "mois"}`}
            value={stats ? `${(stats.monthly_revenue || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $` : "—"}
            sub={stats ? `${stats.monthly_count || 0} bons finalisés` : ""}
            accent
          />
          <Stat icon={ClipboardList} label="Total transports" value={stats?.total_transports ?? "—"} />
          <Stat icon={Truck} label="Aujourd'hui" value={stats?.today ?? "—"} />
          <Stat icon={Users} label="Chauffeurs" value={stats?.drivers ?? "—"} />
          <Stat icon={TrendingUp} label="Actifs" value={items.filter(i => i.status !== "termine").length} />
        </div>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <h2 className="font-display font-black text-xl">Tous les transports</h2>
            <div className="relative w-full md:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="admin-search"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                placeholder="Rechercher client, N° P.O., plaque, chauffeur…"
                className="h-11 pl-10 border-2 rounded-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="label-caps">Bon de transport</TableHead>
                  <TableHead className="label-caps">Date</TableHead>
                  <TableHead className="label-caps">Client</TableHead>
                  <TableHead className="label-caps">Véhicule</TableHead>
                  <TableHead className="label-caps">Plaque</TableHead>
                  <TableHead className="label-caps">Chauffeur</TableHead>
                  <TableHead className="label-caps">Statut</TableHead>
                  <TableHead className="label-caps text-center">Facturé</TableHead>
                  <TableHead className="label-caps text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Aucun transport.</TableCell></TableRow>
                )}
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`admin-row-${t.id}`} className="hover:bg-secondary/50">
                    <TableCell className="font-mono-alt text-xs">
                      {t.po_number ? `P.O. ${t.po_number}` : `#${t.id.slice(-6).toUpperCase()}`}
                    </TableCell>
                    <TableCell>{t.transport_date}</TableCell>
                    <TableCell className="font-semibold">{t.client_name}</TableCell>
                    <TableCell>{t.vehicle_make_model} {t.vehicle_year && <span className="text-muted-foreground">· {t.vehicle_year}</span>}</TableCell>
                    <TableCell className="font-mono-alt uppercase">{t.vehicle_plate || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.driver_email}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-none ${t.status === "termine" ? "bg-black text-white" : "bg-primary text-primary-foreground"}`}>
                        {t.status === "termine" ? "Terminé" : "En cours"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {t.status === "termine" ? (
                        <button
                          type="button"
                          data-testid={`invoice-${t.id}`}
                          onClick={() => toggleInvoiceSent(t)}
                          title={t.invoice_sent ? "Facture envoyée — cliquer pour décocher" : "Marquer la facture comme envoyée"}
                          className={`inline-flex items-center justify-center w-8 h-8 border-2 transition-colors ${
                            t.invoice_sent
                              ? "bg-black text-white border-black hover:bg-black/80"
                              : "bg-card border-border hover:border-black"
                          }`}
                        >
                          {t.invoice_sent ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none border-2 h-9"
                          onClick={() => window.location.assign(`/admin/transports/${t.id}`)}
                          data-testid={`edit-transport-${t.id}`}
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4"/>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none border-2 h-9"
                          onClick={() => openTransportPdf(t.id)}
                          data-testid={`pdf-${t.id}`}
                          title="PDF"
                        >
                          <Download className="w-4 h-4"/>
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-none border-2 h-9 hover:bg-destructive hover:text-destructive-foreground" onClick={()=>del(t.id)} data-testid={`del-${t.id}`} title="Supprimer">
                          <Trash2 className="w-4 h-4"/>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-black text-xl leading-none">Clients</h2>
              <p className="text-xs text-muted-foreground mt-1">Liste affichée dans le menu déroulant du formulaire de transport.</p>
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="new-client-input"
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClient(); } }}
                placeholder="Nom du nouveau client"
                className="h-11 border-2 rounded-none"
              />
              <Button
                data-testid="add-client-btn"
                onClick={addClient}
                disabled={clientBusy || !newClient.trim()}
                className="h-11 rounded-none bg-primary text-primary-foreground btn-hard font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            {clients.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Aucun client enregistré.</div>
            ) : (
              <ul className="divide-y-2 divide-border border-2">
                {clients.map((c) => (
                  <li key={c.id} data-testid={`client-row-${c.id}`} className="flex items-center justify-between px-3 py-2">
                    <span className="font-semibold">{c.name}</span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8"
                        onClick={() => setEditing({ kind: "client", data: { ...c } })}
                        data-testid={`edit-client-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => delClient(c.id)}
                        data-testid={`del-client-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-black text-xl leading-none">Lieux</h2>
              <p className="text-xs text-muted-foreground mt-1">Sélectionner un lieu dans Ramassage / Livraison remplit automatiquement l'adresse et la ville.</p>
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
              <Input
                data-testid="new-loc-name"
                value={newLoc.name}
                onChange={(e) => setNewLoc((l) => ({ ...l, name: e.target.value }))}
                placeholder="Nom du lieu"
                className="h-11 border-2 rounded-none"
              />
              <Input
                data-testid="new-loc-address"
                value={newLoc.address}
                onChange={(e) => setNewLoc((l) => ({ ...l, address: e.target.value }))}
                placeholder="Adresse"
                className="h-11 border-2 rounded-none"
              />
              <Input
                data-testid="new-loc-city"
                value={newLoc.city}
                onChange={(e) => setNewLoc((l) => ({ ...l, city: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }}
                placeholder="Ville"
                className="h-11 border-2 rounded-none"
              />
              <Button
                data-testid="add-loc-btn"
                onClick={addLocation}
                disabled={locBusy || !newLoc.name.trim()}
                className="h-11 rounded-none bg-primary text-primary-foreground btn-hard font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            {locations.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Aucun lieu enregistré.</div>
            ) : (
              <ul className="divide-y-2 divide-border border-2">
                {locations.map((l) => (
                  <li key={l.id} data-testid={`loc-row-${l.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{l.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[l.address, l.city].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8"
                        onClick={() => setEditing({ kind: "location", data: { ...l } })}
                        data-testid={`edit-loc-${l.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => delLocation(l.id)}
                        data-testid={`del-loc-${l.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center shrink-0">
              <UserRound className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-black text-xl leading-none">Contacts</h2>
              <p className="text-xs text-muted-foreground mt-1">Sélectionner un contact dans le formulaire remplit automatiquement nom, courriel et téléphone.</p>
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
              <Input
                data-testid="new-contact-name"
                value={newContact.name}
                onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))}
                placeholder="Nom du contact"
                className="h-11 border-2 rounded-none"
              />
              <Input
                data-testid="new-contact-email"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))}
                placeholder="Courriel"
                className="h-11 border-2 rounded-none"
              />
              <Input
                data-testid="new-contact-phone"
                value={newContact.phone}
                onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addContact(); } }}
                placeholder="Téléphone"
                className="h-11 border-2 rounded-none"
              />
              <Button
                data-testid="add-contact-btn"
                onClick={addContact}
                disabled={contactBusy || !newContact.name.trim()}
                className="h-11 rounded-none bg-primary text-primary-foreground btn-hard font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            {contacts.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Aucun contact enregistré.</div>
            ) : (
              <ul className="divide-y-2 divide-border border-2">
                {contacts.map((c) => (
                  <li key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8"
                        onClick={() => setEditing({ kind: "contact", data: { ...c } })}
                        data-testid={`edit-contact-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => delContact(c.id)}
                        data-testid={`del-contact-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex items-center gap-3">
            <div className="w-10 h-10 bg-primary flex items-center justify-center shrink-0">
              <Car className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <h2 className="font-display font-black text-xl leading-none">Véhicules mémorisés</h2>
              <p className="text-xs text-muted-foreground mt-1">Ajoutés automatiquement lors de l'enregistrement d'un transport. Choisir un n° de flotte remplit ensuite marque/modèle, année, couleur, plaque et NIV.</p>
            </div>
          </div>
          <div className="p-4">
            {vehicles.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                Aucun véhicule mémorisé — enregistrez un transport avec un n° de flotte pour peupler la liste.
              </div>
            ) : (
              <ul className="divide-y-2 divide-border border-2">
                {vehicles.map((v) => (
                  <li key={v.id} data-testid={`vehicle-row-${v.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-alt font-bold uppercase truncate">N° {v.fleet_number}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[v.vehicle_make_model, v.vehicle_year, v.vehicle_color, v.vehicle_plate, v.vehicle_vin]
                          .filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8"
                        onClick={() => setEditing({ kind: "vehicle", data: { ...v } })}
                        data-testid={`edit-vehicle-${v.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-2 h-8 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => delVehicle(v.id)}
                        data-testid={`del-vehicle-${v.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none border-2 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {editing ? editKindTitle[editing.kind] : ""}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {editing.kind === "client" && (
                <div className="space-y-2">
                  <Label className="label-caps">Nom du client</Label>
                  <Input
                    data-testid="edit-client-name"
                    value={editing.data.name || ""}
                    onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))}
                    className="h-11 border-2 rounded-none"
                  />
                </div>
              )}
              {editing.kind === "location" && (
                <>
                  <div className="space-y-2">
                    <Label className="label-caps">Nom du lieu</Label>
                    <Input
                      data-testid="edit-loc-name"
                      value={editing.data.name || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Adresse</Label>
                    <Input
                      data-testid="edit-loc-address"
                      value={editing.data.address || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, address: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Ville</Label>
                    <Input
                      data-testid="edit-loc-city"
                      value={editing.data.city || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, city: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                </>
              )}
              {editing.kind === "contact" && (
                <>
                  <div className="space-y-2">
                    <Label className="label-caps">Nom</Label>
                    <Input
                      data-testid="edit-contact-name"
                      value={editing.data.name || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Courriel</Label>
                    <Input
                      data-testid="edit-contact-email"
                      type="email"
                      value={editing.data.email || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, email: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Téléphone</Label>
                    <Input
                      data-testid="edit-contact-phone"
                      value={editing.data.phone || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, phone: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                </>
              )}
              {editing.kind === "vehicle" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2 col-span-2">
                    <Label className="label-caps">N° de flotte</Label>
                    <Input
                      data-testid="edit-veh-fleet"
                      value={editing.data.fleet_number || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, fleet_number: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label className="label-caps">Marque / Modèle</Label>
                    <Input
                      data-testid="edit-veh-model"
                      value={editing.data.vehicle_make_model || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, vehicle_make_model: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Année</Label>
                    <Input
                      data-testid="edit-veh-year"
                      value={editing.data.vehicle_year || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, vehicle_year: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Couleur</Label>
                    <Input
                      data-testid="edit-veh-color"
                      value={editing.data.vehicle_color || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, vehicle_color: e.target.value } }))}
                      className="h-11 border-2 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Plaque</Label>
                    <Input
                      data-testid="edit-veh-plate"
                      value={editing.data.vehicle_plate || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, vehicle_plate: e.target.value } }))}
                      className="h-11 border-2 rounded-none uppercase"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">NIV</Label>
                    <Input
                      data-testid="edit-veh-vin"
                      value={editing.data.vehicle_vin || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, vehicle_vin: e.target.value } }))}
                      className="h-11 border-2 rounded-none uppercase"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none border-2" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button
              data-testid="edit-save"
              disabled={editBusy}
              onClick={saveEdit}
              className="rounded-none bg-primary text-primary-foreground btn-hard font-bold"
            >
              {editBusy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className={`rounded-none border-2 p-5 ${accent ? "bg-black text-white border-black" : ""}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className={`label-caps truncate ${accent ? "text-white/60" : "text-muted-foreground"}`}>{label}</div>
          <div className="font-display font-black text-3xl md:text-4xl leading-none mt-2 truncate">{value}</div>
          {sub && <div className={`text-xs mt-2 ${accent ? "text-white/70" : "text-muted-foreground"}`}>{sub}</div>}
        </div>
        <div className={`w-12 h-12 flex items-center justify-center shrink-0 ${accent ? "bg-primary" : "bg-secondary"}`}>
          <Icon className={`w-6 h-6 ${accent ? "text-primary-foreground" : "text-foreground"}`} strokeWidth={2.5} />
        </div>
      </div>
    </Card>
  );
}
